/**
 * Where a job stands on PAYROLL and on BILLING — read, never stored.
 *
 *   GET /api/office/job-money   (server/utils/jobMoneyState.js)
 *
 * ── Why these are not job statuses ──────────────────────────────────────────
 *
 * The owner, 2026-09-24: "those were archived as the workers where paid … we
 * mark them invoiced and when invoices paid mark them paid … we need some
 * other kind of status to track payroll and payments". Right, and they are
 * kept apart from `jobs.status` on purpose. The status is the WORK: to do,
 * active, paused, completed, put away. Payroll and billing are facts about
 * money that already live in their own records — an approved timesheet, a
 * paid paystub, an invoice link, that invoice's status. Stored as a second
 * copy on the job they would have to be rewritten by every path that moves
 * money (the Stripe webhook, mark-unpaid, an invoice deleted, a sheet
 * rejected …) and the first one missed would show "Paid" on work nobody was
 * paid for. Derived, they cannot disagree with the money.
 *
 * ── "Processed" ────────────────────────────────────────────────────────────
 *
 * Approving a week still files the COMPLETED jobs on it away (status
 * `archived`, with `archived_timesheet_id` naming the sheet): that is what
 * keeps finished work off next week's timesheets. Such a job was put away BY
 * PAYROLL, not by a person, so it reads "Processed" — and it stays on the
 * invoice picker, which is where finished work belongs until it is billed.
 */

export const JOB_MONEY_ENDPOINT = '/office/job-money';

/** The job id → { billing, payroll } map out of the endpoint's answer. */
export function moneyById(payload) {
  const out = new Map();
  const jobs = payload && payload.jobs;
  if (!jobs || typeof jobs !== 'object') return out;
  for (const [id, state] of Object.entries(jobs)) out.set(String(id), state || {});
  return out;
}

/** A job the approval of a timesheet put away: its payroll has run. */
export function isProcessed(job) {
  return !!job && job.status === 'archived' && job.archived_timesheet_id != null;
}

/** Finished work: the jobs whose billing state is a question worth asking. */
export function isFinished(job) {
  return !!job && (job.status === 'completed' || job.status === 'archived');
}

/**
 * The work status as a person should read it, given the whole job — so a job
 * put away by payroll says "Processed" and one put away by hand says
 * "Archived". `label` is the fallback for every other status (jobFields
 * statusLabel), passed in so this module does not own the status words.
 */
export function jobStatusLabel(job, label) {
  if (isProcessed(job)) return 'Processed';
  return label(job && job.status);
}

const hoursText = (h) => {
  const n = Math.round((Number(h) || 0) * 100) / 100;
  return `${n} h`;
};

/**
 * The billing pill: { label, tone } or null.
 *
 * Only a FINISHED job that is not billed says "Not billed", because that is
 * the one a person should act on; an open job with no invoice yet is normal
 * and saying so on every row is noise.
 */
export function billingPill(job, billing) {
  const state = billing && billing.state;
  const latest = billing && billing.latest;
  if (state === 'paid') return { label: 'Paid', tone: 'ok' };
  // Every invoice on it was cancelled. Still linked — the invoice picker will
  // not offer it again, so it is not "Not billed" — but nobody owes that bill,
  // and "Invoiced" would say they do. `latest` is only ever a cancelled one
  // when EVERY link is (server/utils/jobMoneyState.js).
  //
  // Two lengths. The list pill is uppercase and tracked, and "INVOICE
  // CANCELLED · INV-2026-0002" ran out of a 320px card in the browser
  // (ui-check job-money, #706) — so the pill has the shape "Invoiced · INV-…"
  // already proved fits, and the record, which has room, says it in full.
  if (state === 'invoiced' && latest && latest.status === 'cancelled') {
    return latest.number
      ? { label: `Cancelled · ${latest.number}`, long: `Invoice cancelled · ${latest.number}`, tone: 'warn' }
      : { label: 'Cancelled', long: 'Invoice cancelled', tone: 'warn' };
  }
  if (state === 'invoiced') {
    return { label: latest && latest.number ? `Invoiced · ${latest.number}` : 'Invoiced', tone: 'sched' };
  }
  if (isFinished(job)) return { label: 'Not billed', tone: 'warn' };
  return null;
}

/** "3 h to approve", or "To approve" when the work is a 0-hour fixed-pay line. */
const hoursThen = (h, words) => (Number(h) > 0 ? `${hoursText(h)} ${words}` : words[0].toUpperCase() + words.slice(1));

/**
 * The payroll pill: { label, tone } or null (a job with no hours says nothing).
 *
 * PENDING says WHY, from `pending_on`: only a submitted week is "to approve".
 * A rejected one is back with the worker, and a draft has not been handed in
 * — every one of them used to read "N h to approve", which sent a manager to
 * approve a week nobody could approve yet (review, #705).
 */
export function payrollPill(payroll) {
  const state = payroll && payroll.state;
  if (state === 'pending') {
    const lead = {
      submitted: [payroll.hours_to_approve, 'to approve', 'warn'],
      rejected: [payroll.hours_sent_back, 'sent back', 'warn'],
      draft: [payroll.hours_unsubmitted, 'not submitted', 'neutral'],
    }[payroll.pending_on];
    if (!lead) return { label: hoursThen(payroll.hours_pending, 'not approved yet'), tone: 'warn' };
    // The rest of the pending hours — another worker's sheet in another state.
    // A crew of three (submitted, rejected, draft, 8 h each) read "8 h to
    // approve" and hid 16 h (review, #706). Each figure is rounded to the
    // hundredth on its own, so a difference of 0.01 is rounding, not hours.
    const rest = Math.round(((Number(payroll.hours_pending) || 0) - (Number(lead[0]) || 0)) * 100) / 100;
    const more = rest >= 0.02 ? ` · ${hoursText(rest)} more not approved yet` : '';
    return { label: hoursThen(lead[0], lead[1]) + more, tone: lead[2] };
  }
  if (state === 'processed') return { label: 'Payroll processed', tone: 'sched' };
  if (state === 'paid') return { label: 'Crew paid', tone: 'ok' };
  return null;
}

/**
 * A job the endpoint has no row for: the empty state of each half, the same
 * shapes server/utils/jobMoneyState.js emptyBilling() / emptyPayroll() return
 * and docs/REBUILD_API.md §3 declares. One copy here rather than a literal at
 * each call site, where it drifted (review, #706).
 */
export const EMPTY_BILLING = Object.freeze({ state: 'unbilled', invoice_count: 0, latest: null });
export const EMPTY_PAYROLL = Object.freeze({
  state: 'none', hours_pending: 0, hours_approved: 0,
  hours_to_approve: 0, hours_sent_back: 0, hours_unsubmitted: 0, pending_on: null,
  processed_by_timesheet: null,
});

/** The Jobs list billing filter. */
export const BILLING_FILTER_VALUES = ['', 'unbilled', 'invoiced', 'paid'];
export const BILLING_FILTER_LABELS = {
  '': 'Any',
  unbilled: 'Finished, not billed',
  invoiced: 'Invoiced, not paid',
  paid: 'Paid',
};

/**
 * Does this job pass the billing filter?
 *
 * "Finished, not billed" is the list the office works from: completed or put
 * away (by hand or by payroll), and on no invoice. A job the endpoint has no
 * row for has neither an invoice nor hours, which is unbilled.
 */
export function matchesBilling(job, billing, value) {
  if (!value) return true;
  const state = (billing && billing.state) || 'unbilled';
  if (value === 'unbilled') return isFinished(job) && state === 'unbilled';
  return state === value;
}
